import type { LanguageData } from "./types";

export const languageData: LanguageData = {
  slug: "cs-basics",
  name: "コンピュータサイエンス基礎",
  nameEn: "Computer Science",
  seo: {
    title:
      "コンピュータサイエンス基礎を学べる女性向けプログラミングスクール | ゆめさくエンジニア",
    description:
      "アルゴリズム・データ構造・ネットワーク・OSの基礎を体系的に学び、エンジニアとしての土台を築きましょう。ゆめさくエンジニアで実践的にCS基礎を身につけられます。",
    url: "/columns/cs-basics-programming-school",
  },
  hero: {
    title: "コンピュータサイエンス基礎を学べる女性向けプログラミングスクール",
    englishTitle: "Learn Computer Science",
  },
  breadcrumb: [
    { name: "ホーム", url: "/" },
    { name: "プログラミング言語・技術別", url: "/columns/category/lang" },
    { name: "コンピュータサイエンス基礎" },
  ],
  overview: {
    heading: "コンピュータサイエンス基礎とは？",
    paragraphs: [
      "コンピュータサイエンス（CS）基礎とは、コンピュータの仕組みやプログラムの動作原理を体系的に理解するための学問分野です。アルゴリズム、データ構造、ネットワーク、オペレーティングシステム（OS）などの知識が含まれます。",
      "プログラミング言語やフレームワークは数年で変わりますが、CS基礎の知識は普遍的です。なぜこのコードが速いのか、なぜこのバグが起きるのか、なぜこの設計が優れているのか——その「なぜ」を理解する力がCS基礎で身につきます。",
      "特に技術面接ではアルゴリズム・データ構造の問題が出題されることが多く、CS基礎の有無がキャリアの幅を大きく左右します。",
    ],
    useCases: [
      {
        label: "アルゴリズムとデータ構造",
        description:
          "ソート、探索、グラフ理論などのアルゴリズムと、配列、リスト、木、ハッシュなどのデータ構造を理解し、効率的なプログラムを書く基盤を作ります。",
      },
      {
        label: "ネットワークの基礎",
        description:
          "TCP/IP、HTTP、DNS、ファイアウォールなど、インターネットの仕組みを理解し、Webアプリケーション開発やインフラ構築に活かします。",
      },
      {
        label: "OSの仕組み",
        description:
          "プロセス管理、メモリ管理、ファイルシステムなど、OSの基本概念を理解し、パフォーマンス最適化やトラブルシューティングに役立てます。",
      },
      {
        label: "計算量・設計パターン",
        description:
          "Big-O記法による計算量の評価や、ソフトウェア設計パターンを理解し、スケーラブルなシステムを設計する力を身につけます。",
      },
    ],
  },
  whyLearn: {
    heading: "コンピュータサイエンス基礎を学ぶべき3つの理由",
    items: [
      {
        title: "技術の変化に左右されない普遍的な力",
        description:
          "フレームワークやライブラリは流行り廃りがありますが、CS基礎は何十年も変わらない普遍的な知識です。一度身につければ、新しい技術を学ぶスピードも格段に向上します。",
      },
      {
        title: "技術面接・コーディングテストで差がつく",
        description:
          "GAFAMをはじめとする多くのIT企業では、採用面接でアルゴリズム・データ構造の問題が出題されます。CS基礎の知識があることで、より良い企業・ポジションへの道が開けます。",
      },
      {
        title: "「なぜ動くのか」を理解できるエンジニアへ",
        description:
          "コードをコピペして動かすだけでなく、なぜそのコードが動くのか、なぜその設計が最適なのかを理解できるようになります。チーム内で頼られるエンジニアへの第一歩です。",
      },
    ],
  },
  luagateCurriculum: {
    heading: "ゆめさくエンジニアでコンピュータサイエンス基礎を学ぶ",
    paragraphs: [
      "ゆめさくエンジニアでは、オンボーディングからPhase 1にかけて、プログラミングと並行してCS基礎を段階的に学びます。座学だけでなく、Pythonを使ったアルゴリズムの実装演習を通じて「使える知識」として定着させます。",
      "数学が苦手な方でも安心できるよう、図解やビジュアルを多用した分かりやすい教材を用意しています。",
    ],
    topics: [
      "基本データ構造（配列・リスト・スタック・キュー・木・ハッシュ）",
      "ソート・探索アルゴリズム",
      "計算量（Big-O記法）の理解",
      "ネットワーク基礎（TCP/IP・HTTP・DNS）",
      "OS基礎（プロセス・メモリ・ファイルシステム）",
      "データベースの基礎概念",
      "実践演習：アルゴリズム問題を解きながら理解を深める",
    ],
  },
  careerPaths: {
    heading: "コンピュータサイエンス基礎を活かせるキャリア",
    items: [
      {
        title: "全エンジニア職の基盤",
        description:
          "CS基礎はフロントエンド・バックエンド・インフラ・AI/MLなど、あらゆるエンジニア職の土台となる知識です。CS基礎があるエンジニアは技術面接で高い評価を得やすく、実務でもバグの原因特定やパフォーマンス改善で差がつきます。",
      },
      {
        title: "ソフトウェアエンジニア",
        description:
          "CS基礎の知識を活かして効率的なアルゴリズムやデータ構造を選択し、スケーラブルなソフトウェアを設計・開発する職種です。",
        salary: "450〜800万円",
      },
      {
        title: "インフラ/SREエンジニア",
        description:
          "ネットワーク・OS・システム設計の知識を活かして、サービスの安定稼働と拡張性を支えるインフラ基盤を構築・運用します。",
        salary: "500〜900万円",
      },
    ],
  },
  comparison: {
    heading: "独学 vs ゆめさくエンジニア",
    items: [
      {
        label: "学習期間",
        selfStudy: "6〜12ヶ月（大学教科書の独学は非効率になりやすい）",
        luagate: "2〜3ヶ月（実務に直結する内容を厳選して効率的に学習）",
      },
      {
        label: "挫折率",
        selfStudy: "数学的な記法や理論の壁で8割以上が挫折",
        luagate: "図解・コード実装を併用し、理論を実感に変える学習法",
      },
      {
        label: "実践プロジェクト",
        selfStudy: "教科書の演習問題を解くだけになりがち",
        luagate: "Pythonでアルゴリズムを実装し、動くコードで理解",
      },
      {
        label: "質問対応",
        selfStudy: "「なぜこのアルゴリズムが最適か」がわからない",
        luagate: "メンターが計算量や設計の根拠を丁寧に解説",
      },
      {
        label: "就職サポート",
        selfStudy: "技術面接でのCS質問への対策が難しい",
        luagate: "模擬面接・コーディングテスト対策を含む就職支援",
      },
    ],
  },
  faq: [
    {
      question: "文系出身でもコンピュータサイエンスを学べますか？",
      answer:
        "もちろん学べます。ゆめさくエンジニアのカリキュラムは数学の前提知識を最小限に抑え、プログラミングを通じて概念を理解するアプローチを採用しています。文系出身の受講生も学んでいます。",
    },
    {
      question: "CS基礎を学ぶとプログラミングスキルも上がりますか？",
      answer:
        "大きく向上します。データ構造の特性を理解すれば適切なデータ型を選べるようになり、アルゴリズムの知識があればより効率的なコードが書けるようになります。「なんとなく動くコード」から「根拠のある設計」へとレベルアップできます。",
    },
    {
      question: "CS基礎は実務のどんな場面で役立ちますか？",
      answer:
        "パフォーマンス改善（なぜこの処理が遅いのか）、バグの原因特定（メモリリーク・デッドロック）、システム設計（適切なデータ構造・アーキテクチャの選択）など、日常的な開発業務のあらゆる場面で役立ちます。",
    },
    {
      question: "CS基礎の学習にはどのプログラミング言語を使いますか？",
      answer:
        "ゆめさくエンジニアではPythonを使ってアルゴリズムやデータ構造を実装しながら学びます。Pythonは文法がシンプルで、CS概念の理解に集中しやすいため、学習用の言語として最適です。",
    },
  ],
  relatedLanguages: [
    { slug: "python", name: "Python" },
    { slug: "java", name: "Java" },
    { slug: "linux", name: "Linux" },
    { slug: "system-design", name: "システム設計" },
  ],
};
